import React, { useState, forwardRef, useEffect } from "react";
import {
  FormGroup,
  Label,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Badge
} from "reactstrap";
import { useThemeContext } from "../../Context/ThemeContext";
import { getDarkInput } from "../../Utils/otherFunctions";

const MultiSelectionField = forwardRef(function MultiSelectField(props, ref) {
  const {
    label,
    className,
    fieldState,
    placeholder,
    options = {},
    isRequired,
    onChange,
    value,
    defaultChecked = []
  } = props;
  
  const { themeContext } = useThemeContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);

  // Enhanced function to ensure proper format of options
  const formatOption = (option) => {
    if (!option || typeof option !== 'object') return null;
    
    // Ensure name property exists
    const formattedOption = {
      ...option,
      name: option.name || option.nama || "Unknown",
      group: option.group || ""
    };
    
    return formattedOption;
  };

  // Initialize with value or defaultChecked
  useEffect(() => {
    let initialOptions = [];
    
    if (value && value.length > 0) {
      initialOptions = value.map(formatOption).filter(Boolean);
    } else if (defaultChecked && defaultChecked.length > 0) {
      // Format and filter defaultChecked items
      initialOptions = defaultChecked
        .map(formatOption)
        .filter(Boolean);
    }
    
    setSelectedOptions(initialOptions);
    if (initialOptions.length > 0 && onChange) {
      onChange(initialOptions);
    }
  }, []);

  // Update selected options when defaultChecked changes (for dynamic updates)
  useEffect(() => {
    if (defaultChecked && defaultChecked.length > 0 && selectedOptions.length === 0) {
      const formattedOptions = defaultChecked
        .map(formatOption)
        .filter(Boolean);
      
      setSelectedOptions(formattedOptions);
      if (formattedOptions.length > 0 && onChange) {
        onChange(formattedOptions);
      }
    }
  }, [defaultChecked]);

  const filterGroupedOptions = () => {
    const filtered = {};
    for (const group in options) {
      const filteredGroup = options[group].filter((option) =>
        (option.nama || option.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filteredGroup.length > 0) {
        filtered[group] = filteredGroup;
      }
    }
    return filtered;
  };

  const filteredOptions = filterGroupedOptions();

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const toggleOption = (e, option) => {
    e.preventDefault();
    e.stopPropagation();
    
    const formattedOption = formatOption({ ...option, group: option.group });
    
    const newSelectedOptions = selectedOptions.some(
      (item) => item.id === option.id && item.group === option.group
    )
      ? selectedOptions.filter(
          (item) => !(item.id === option.id && item.group === option.group)
        )
      : [...selectedOptions, formattedOption];

    setSelectedOptions(newSelectedOptions);
    onChange?.(newSelectedOptions);
  };

  // Debug function
  const logSelectedOptions = () => {
    console.log("Selected Options:", selectedOptions);
  };

  return (
    <FormGroup className={`d-flex flex-column ${className}`}>
      {label && (
        <Label className="bg-transparent">
          {label}{" "}
          {isRequired && (
            <font className="ml-1" color="red">
              *
            </font>
          )}
        </Label>
      )}
      
      <div className="w-100">
        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
          <DropdownToggle
            caret
            color="primary"
            className={`w-100 d-flex justify-content-between align-items-center ${getDarkInput(
              themeContext
            )}`}
            onClick={logSelectedOptions} // Debug
          >
            {selectedOptions.length > 0
              ? `${selectedOptions.length} selected`
              : placeholder || "Select options..."}
          </DropdownToggle>

          <DropdownMenu className="w-100">
            <div className="p-2 border-bottom">
              <Input
                type="text"
                placeholder="Search options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={getDarkInput(themeContext)}
                style={{ border: '1px solid black' }}
              />
            </div>

            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {Object.entries(filteredOptions).map(([group, groupOptions]) => (
                <div key={group}>
                  <div className="px-3 py-2 bg-light text-dark fw-bold position-sticky top-0">
                    {group}
                  </div>
                  {groupOptions.map((option) => {
                    const isSelected = selectedOptions.some(
                      (item) => item.id === option.id
                    );
                    return (
                      <div 
                        key={`${group}-${option.id}`}
                        className="dropdown-item d-flex align-items-center"
                        onClick={(e) => toggleOption(e, { ...option, group })}
                        role="button"
                      >
                        <Input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => e.stopPropagation()}
                          className={`btn-primary mx-4 mb-2 ${getDarkInput(
                            themeContext
                          )} ${className}`}
                          id={`${group}-${option.id}`}
                        />
                        <Label 
                          className="mb-0 w-100" 
                          onClick={(e) => e.preventDefault()}
                        >
                          {option.nama || option.name}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </DropdownMenu>
        </Dropdown>
      </div>

      {selectedOptions.length > 0 && (
        <div className="mt-3">
          <div className="d-flex flex-wrap gap-2">
            {selectedOptions.map((option, index) => {
              // Skip rendering if option is invalid
              if (!option || !option.id) return null;
              
              const displayName = option.nama || option.name || "Unknown";
              const groupName = option.group || "";
              
              return (
                <Badge
                  key={`${index}-${option.id}`}
                  color="primary"
                  className="d-flex align-items-center"
                >
                  {displayName}{groupName ? ` (${groupName})` : ""}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {fieldState?.error && (
        <Label className="label-text text-danger">
          {fieldState.error.message}
        </Label>
      )}
    </FormGroup>
  );
});

export default MultiSelectionField;